import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  Chip,
  IconButton,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useSelector, useDispatch } from 'react-redux';
import { getSupplierCatalog, getSupplierInfo } from '../../api/suppliers';
import { addToCart } from '../../redux/slices/catalogCartSlice';
import Loading from '../../components/Loading';
import { PublicCatalogLayout } from './PublicCatalogLayout';
import notImage from '../../assets/product-default.png';
import { CartDrawer } from '../../components/CartDrawer';
import { FloatingCartButton, CartButton } from '../../components/CartButton';
import toast from 'react-hot-toast';
import styles from './catalogProductDetail.module.scss';

const categoryEmojis = {
  'Frutas y Verduras': '🥬',
  Carnicos: '🥩',
  Abarrote: '🍞',
  Abarrotes: '🍞',
  Bebidas: '🥤',
  'Material operativo': '🧂',
  'Pescados y Mariscos': '🐟',
};

export const CatalogProductDetail = () => {
  const { supplierId, productId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const auth = useSelector((state) => state?.auth);
  const token = auth?.token || null;
  const restaurant = auth?.restaurant || null;
  const restaurantList = auth?.restaurantList || [];
  const isAuthenticated = auth?.status === 'authenticated';

  const currentRestaurant = restaurantList.find((val) => val.id === parseInt(restaurant));
  const restaurantId = currentRestaurant?.id || null;

  const [product, setProduct] = useState(null);
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    loadData();
  }, [productId, supplierId, restaurantId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catalogData, supplierData] = await Promise.all([
        getSupplierCatalog(
          supplierId,
          { limit: 1000, page: 1, categoryId: '', restaurantId: restaurantId },
          token,
        ),
        getSupplierInfo(supplierId, token),
      ]);

      if (supplierData) {
        setSupplierInfo(supplierData);
      }

      let productsArray = [];
      if (catalogData && catalogData.products && Array.isArray(catalogData.products)) {
        productsArray = catalogData.products;
      } else if (Array.isArray(catalogData)) {
        productsArray = catalogData;
      }

      const foundProduct = productsArray.find((p) => p.id === parseInt(productId));
      if (foundProduct) {
        setProduct(foundProduct);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    }
    setLoading(false);
  };

  const handleBack = () => {
    navigate(`/proveedor/${supplierId}/catalogo`);
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para agregar productos al carrito');
      sessionStorage.setItem(
        'redirectAfterLogin',
        `/proveedor/${supplierId}/catalogo/${productId}`,
      );
      navigate('/ingreso');
      return;
    }

    for (let i = 0; i < quantity; i++) {
      dispatch(addToCart(product));
    }

    toast.success(
      `${quantity} ${product.measurementUnit}${quantity > 1 ? 's' : ''} de ${
        product.name
      } agregado${quantity > 1 ? 's' : ''} al carrito`,
    );
    setQuantity(1);
  };

  if (loading) {
    return (
      <PublicCatalogLayout supplierName={supplierInfo?.name}>
        <Box
          sx={{
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loading />
        </Box>
      </PublicCatalogLayout>
    );
  }

  if (!product) {
    return (
      <PublicCatalogLayout supplierName={supplierInfo?.name}>
        <Box sx={{ padding: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="text.secondary">
            Producto no encontrado
          </Typography>
          <Button onClick={handleBack} sx={{ marginTop: 2 }}>
            Volver al catálogo
          </Button>
        </Box>
      </PublicCatalogLayout>
    );
  }

  const emoji = categoryEmojis[product.categoryName] || '📦';

  return (
    <PublicCatalogLayout supplierName={supplierInfo?.name}>
      {isAuthenticated && <FloatingCartButton />}
      <CartDrawer />

      <Box sx={{ paddingY: 4, paddingX: { xs: 2, md: 8 }, minHeight: '80vh' }}>
        {/* Back Button and Cart Button Row */}
        <Box
          sx={{
            marginBottom: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              color: '#49a35b',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e8f5e8',
              },
            }}
          >
            Volver al catálogo
          </Button>

          {isAuthenticated && (
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <CartButton color="#49a35b" />
            </Box>
          )}
        </Box>

        <Grid container spacing={4}>
          {/* Product Image */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                backgroundColor: '#f8f9fa',
              }}
            >
              <Box
                component="img"
                src={product.image || notImage}
                alt={product.name}
                sx={{
                  width: '100%',
                  height: { xs: '320px', md: '450px' },
                  objectFit: 'contain',
                }}
              />
            </Card>
          </Grid>

          {/* Product Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Category Badge */}
              <Chip
                label={`${emoji} ${product.categoryName || product.category}`}
                sx={{
                  backgroundColor: '#49a35b',
                  color: '#fff',
                  fontWeight: 'bold',
                  marginBottom: 2,
                  width: 'fit-content',
                  fontSize: '14px',
                }}
              />

              {/* Product Name */}
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  color: '#333',
                  marginBottom: 2,
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                }}
              >
                {product.name}
              </Typography>

              {/* Supplier Info */}
              {supplierInfo && (
                <Box sx={{ marginBottom: 3 }}>
                  <Typography variant="body1" sx={{ color: '#666', marginBottom: 1 }}>
                    <strong>Proveedor:</strong>
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#49a35b', fontWeight: 'bold' }}>
                    {supplierInfo.name}
                  </Typography>
                </Box>
              )}

              {/* Price */}
              <Box sx={{ marginBottom: 3 }}>
                <Typography variant="body1" sx={{ color: '#666', marginBottom: 1 }}>
                  <strong>Precio:</strong>
                </Typography>
                <Typography variant="h4" sx={{ color: '#49a35b', fontWeight: 'bold' }}>
                  ${product.price}
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{ color: '#999', marginLeft: 1 }}
                  >
                    por {product.measurementUnit || 'unidad'}
                  </Typography>
                </Typography>
                {product.iva > 0 && (
                  <Typography variant="body2" sx={{ color: '#666', marginTop: 0.5 }}>
                    + IVA {product.iva}%
                  </Typography>
                )}
              </Box>

              {/* Product Description */}
              {product.description && (
                <Box
                  sx={{
                    backgroundColor: '#f8fdf9',
                    padding: 2.5,
                    borderRadius: 2,
                    marginBottom: 3,
                    border: '1px solid #e0f2e4',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                    <Box
                      sx={{
                        width: 4,
                        height: 20,
                        backgroundColor: '#49a35b',
                        borderRadius: 1,
                        marginRight: 1.5,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 'bold',
                        color: '#49a35b',
                        fontSize: '16px',
                      }}
                    >
                      Descripción del producto
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#555',
                      lineHeight: 1.8,
                      fontSize: '15px',
                      paddingLeft: 2.5,
                    }}
                  >
                    {product.description}
                  </Typography>
                </Box>
              )}

              {/* Quantity Selector */}
              {isAuthenticated && (
                <Box sx={{ marginBottom: 3 }}>
                  <Typography
                    variant="body1"
                    sx={{ color: '#666', marginBottom: 1, fontWeight: 'bold' }}
                  >
                    Cantidad:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                      sx={{
                        backgroundColor: '#e8f5e8',
                        color: '#49a35b',
                        '&:hover': {
                          backgroundColor: '#d4edd4',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#f5f5f5',
                          color: '#ccc',
                        },
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>

                    <TextField
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val) && val >= 1) {
                          handleQuantityChange(val);
                        }
                      }}
                      type="number"
                      inputProps={{
                        min: 1,
                        style: {
                          textAlign: 'center',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          width: '80px',
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '& fieldset': {
                            borderColor: '#49a35b',
                            borderWidth: '2px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#3d8a4d',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#49a35b',
                          },
                        },
                      }}
                    />

                    <IconButton
                      onClick={() => handleQuantityChange(quantity + 1)}
                      sx={{
                        backgroundColor: '#49a35b',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#3d8a4d',
                        },
                      }}
                    >
                      <AddIcon />
                    </IconButton>

                    <Typography variant="body2" sx={{ color: '#666', marginLeft: 1 }}>
                      {product.measurementUnit}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Info Box for non-authenticated users */}
              {!isAuthenticated && (
                <Box
                  sx={{
                    backgroundColor: '#f8f9fa',
                    padding: 3,
                    borderRadius: 3,
                    marginBottom: 3,
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#666', marginBottom: 1 }}>
                    💡 <strong>¿Quieres comprar este producto?</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Inicia sesión para agregar productos al carrito y realizar tu pedido.
                  </Typography>
                </Box>
              )}

              {/* Add to Cart Button */}
              <Button
                onClick={handleAddToCart}
                fullWidth
                variant="contained"
                size="large"
                startIcon={<ShoppingCartIcon />}
                sx={{
                  backgroundColor: '#49a35b',
                  color: '#fff',
                  borderRadius: 10,
                  height: 56,
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginTop: 'auto',
                  '&:hover': {
                    backgroundColor: '#3d8a4d',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(73, 163, 91, 0.3)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                {isAuthenticated ? 'Agregar al carrito' : 'Iniciar sesión para comprar'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </PublicCatalogLayout>
  );
};
